import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { redtailApi, queryKeys } from '@client/api/integrations';
import { useDebounce } from '@client/hooks/useDebounce';
import { Contacts } from '../components/integrations/Contacts';
import { LinkClientModal } from '../components/integrations/LinkClientModal';
import type { IntegrationContact } from '@client/api/types';
import type { Redtail } from '@client/api/types';

function getPrimaryEmail(emails: Redtail.Contact['emails']): string | undefined {
  if (!emails || emails.length === 0) return undefined;
  const primary = emails.find(e => e.isPrimary);
  return primary?.address ?? emails[0]?.address;
}

function normalizeRedtailContact(contact: Redtail.Contact): IntegrationContact {
  return {
    id: contact.id,
    firstName: contact.firstName,
    lastName: contact.lastName,
    companyName: contact.companyName,
    email: getPrimaryEmail(contact.emails),
    type: contact.type,
    status: contact.status,
    waterlilyClientId: contact.waterlilyClientId,
  };
}

export function AdvisorIntegrationsRedtailContacts() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [contactToLink, setContactToLink] = useState<IntegrationContact | null>(null);
  const queryClient = useQueryClient();

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.redtail.list('contacts', { page, pageSize, query: debouncedSearch }),
    queryFn: () =>
      redtailApi.contacts.list({
        page: String(page),
        pageSize: String(pageSize),
        query: debouncedSearch || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const linkMutation = useMutation({
    mutationFn: ({ contactId, clientId }: { contactId: string; clientId: string }) =>
      redtailApi.contacts.link({ contactId, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.redtail.lists('contacts') });
      setContactToLink(null);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (contactId: string) => redtailApi.contacts.unlink(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.redtail.lists('contacts') });
    },
  });

  const contacts = (data?.contacts ?? []).map(normalizeRedtailContact);
  const totalPages = data?.meta?.totalPages ?? 1;

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <div className="my-7 flex flex-col items-center justify-between gap-8 md:flex-row md:gap-0">
        <div className="flex items-center gap-5">
          <h1 className="text-primary-dark text-4xl font-semibold">Redtail Contacts</h1>
        </div>
      </div>
      <div className="bg-primary-100 my-6 h-px w-full" />

      <Contacts
        contacts={contacts}
        totalPages={totalPages}
        isFetching={isFetching}
        error={error}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        search={search}
        onSearchChange={setSearch}
        onRefetch={refetch}
        onLink={setContactToLink}
        onUnlink={contactId => unlinkMutation.mutate(contactId)}
        isUnlinking={unlinkMutation.isPending}
        crmName="Redtail"
        getExternalUrl={id => `https://review.crm.redtailtechnology.com/contacts/${id}`}
      />

      <LinkClientModal
        open={!!contactToLink}
        onClose={() => setContactToLink(null)}
        contact={contactToLink}
        onLink={(contactId, clientId) => linkMutation.mutate({ contactId, clientId })}
        isLinking={linkMutation.isPending}
        linkError={linkMutation.error}
        onReset={() => linkMutation.reset()}
      />
    </main>
  );
}
